import { useCallback, useEffect, useRef, useState } from "react";
import { pickBestAudioRecorderMime, getExtensionFromMimeType, detectMimeFromBytes } from "../../utils/mediaTools/mimeUtils";

type RecorderStatus = "idle" | "recording" | "paused" | "stopped" | "unsupported" | "error";

interface UseAudioRecorderOptions {
  timesliceMs?: number;    // optional chunking (we avoid it by default)
  maxDurationMs?: number;  // optional auto-stop based on recorded time
}

export function useAudioRecorder(options?: UseAudioRecorderOptions) {
  const { timesliceMs, maxDurationMs } = options || {};
  const [status, setStatus] = useState<RecorderStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [mimeType, setMimeType] = useState<string | undefined>(undefined);
  const [durationMs, setDurationMs] = useState<number>(0);

  const chunksRef = useRef<BlobPart[]>([]);
  const mrRef = useRef<MediaRecorder | null>(null);

  // time bookkeeping
  const baseElapsedMsRef = useRef<number>(0);
  const startedAtRef = useRef<number | null>(null);

  // timers/stream
  const tickRef = useRef<number | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // prevent double-stop
  const stopPromiseRef = useRef<Promise<File | null> | null>(null);

  // ---- utils
  const clearTick = () => {
    if (tickRef.current) {
      window.clearInterval(tickRef.current);
      tickRef.current = null;
    }
  };

  const stopStream = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  const startTick = () => {
    clearTick();
    tickRef.current = window.setInterval(() => {
      const base = baseElapsedMsRef.current;
      const nowSeg = startedAtRef.current ? Date.now() - startedAtRef.current : 0;
      const total = base + nowSeg;
      setDurationMs(total);

      // auto-stop based on recorded time
      if (typeof maxDurationMs === "number" && maxDurationMs > 0 && total >= maxDurationMs) {
        // fire-and-forget; component callers will await stop when saving
        void stop();
      }
    }, 250);
  };

  useEffect(() => {
    return () => {
      try { mrRef.current?.stop?.(); } catch {}
      clearTick();
      stopStream();
    };
  }, []);

  // ---- controls
  const start = useCallback(async () => {
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      setStatus("unsupported");
      setError("MediaDevices.getUserMedia is not supported.");
      return;
    }
    try {
      setError(null);

      // get mic
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      streamRef.current = stream;

      // choose a supported mime (defensive)
      let chosenMime = pickBestAudioRecorderMime();
      if (chosenMime && (window as any).MediaRecorder?.isTypeSupported) {
        if (!(window as any).MediaRecorder.isTypeSupported(chosenMime)) {
          chosenMime = ""; // fallback to default
        }
      }
      setMimeType(chosenMime || undefined);

      const mr = chosenMime ? new MediaRecorder(stream, { mimeType: chosenMime }) : new MediaRecorder(stream);
      mrRef.current = mr;
      chunksRef.current = [];

      // collect chunks
      mr.ondataavailable = (ev: BlobEvent) => {
        if (ev.data && ev.data.size > 0) {
          chunksRef.current.push(ev.data);
        }
      };

      mr.onerror = () => {
        setStatus("error");
        setError("Recorder error.");
      };

      mr.onstart = () => {
        startedAtRef.current = Date.now();
        baseElapsedMsRef.current = 0;
        setDurationMs(0);
        setStatus("recording");
        startTick();
      };

      mr.onpause = () => setStatus("paused");
      mr.onresume = () => {
        setStatus("recording");
        startTick();
      };

      // IMPORTANT: do NOT rely on mr.onstop to build the file here.
      // We'll await it inside stop() for the final chunk.

      // avoid timeslice to keep ordering simple & flush manually at stop()
      if (typeof timesliceMs === "number" && timesliceMs > 0) {
        mr.start(timesliceMs);
      } else {
        mr.start();
      }
    } catch (e: any) {
      setStatus("error");
      setError(e?.message || "Failed to start recorder.");
    }
  }, [maxDurationMs, timesliceMs]);

  const pause = useCallback(() => {
    const mr = mrRef.current;
    if (!mr || mr.state !== "recording") return;

    if (startedAtRef.current) {
      baseElapsedMsRef.current += Date.now() - startedAtRef.current;
      startedAtRef.current = null;
    }
    clearTick();
    try { mr.pause(); } catch {}
    setStatus("paused");
    setDurationMs(baseElapsedMsRef.current);
  }, []);

  const resume = useCallback(() => {
    const mr = mrRef.current;
    if (!mr || mr.state !== "paused") return;

    startedAtRef.current = Date.now();
    try { mr.resume(); } catch {}
    setStatus("recording");
    startTick();
  }, []);

  // ASYNC stop: wait for final dataavailable + stop before building Blob/File --------------------
  const stop = useCallback(async (): Promise<File | null> => {
    if (stopPromiseRef.current) return stopPromiseRef.current;
    const mr = mrRef.current;
    if (!mr || mr.state === "inactive") return null;

    // finalize elapsed ...
    if (startedAtRef.current) {
      baseElapsedMsRef.current += Date.now() - startedAtRef.current;
      startedAtRef.current = null;
    }
    clearTick();

    stopPromiseRef.current = new Promise<File | null>((resolve, reject) => {
      const localChunks: BlobPart[] = [...chunksRef.current];

      const onData = (ev: BlobEvent) => {
        if (ev.data && ev.data.size > 0) localChunks.push(ev.data);
      };

      const onStop = async () => {
        mr.removeEventListener("dataavailable", onData);
        mr.removeEventListener("stop", onStop);

        try {
          // ---- figure out the right mime ----
          let mime = (mr.mimeType || mimeType || "").toLowerCase();

          // fallback to first chunk type if needed
          if (!mime || mime === "application/octet-stream") {
            const firstType = (localChunks[0] as any)?.type as string | undefined;
            if (firstType) mime = firstType.toLowerCase();
          }

          const hasVideo = !!streamRef.current?.getVideoTracks()?.length;

          // Coerce video/* → audio/* for audio-only (Safari can yield video/mp4)
          if (!hasVideo && mime.startsWith("video/")) {
            if (mime === "video/webm") mime = "audio/webm";
            else if (mime === "video/ogg") mime = "audio/ogg";
            else if (mime === "video/mp4" || mime === "video/quicktime") mime = "audio/mp4";
          }

          // Build a blob once from chunks (no full arrayBuffer of entire file)
          let blob = new Blob(localChunks, { type: mime || "application/octet-stream" });

          // Lightweight sniff: only head bytes (fast, Safari-friendly)
          try {
            const head = blob.slice(0, 64);
            const headBuf = await head.arrayBuffer();
            const sniffed = detectMimeFromBytes(headBuf);
            if (sniffed) {
              if (!hasVideo && sniffed.startsWith("video/")) {
                if (sniffed === "video/webm") mime = "audio/webm";
                else if (sniffed === "video/ogg") mime = "audio/ogg";
                else if (sniffed === "video/mp4" || sniffed === "video/quicktime") mime = "audio/mp4";
              } else {
                mime = sniffed;
              }
              // ✳️ no need to rebuild bytes; we'll set final File type below
            }
          } catch {
            // ignore sniff errors
          }

          // Final File directly from original chunks (no big copies)
          const ext = getExtensionFromMimeType(mime) || (!hasVideo ? "webm" : "webm"); // audio/mp4 -> m4a
          const file = new File(localChunks, `recording_${Date.now()}.${ext}`, { type: mime });

          if (file.size === 0) {
            setStatus("error");
            setError("Empty recording (no chunks).");
            resolve(null);
            return;
          }

          // cleanup
          chunksRef.current = [];
          setDurationMs(baseElapsedMsRef.current);
          baseElapsedMsRef.current = 0;
          setStatus("stopped");
          // stop tracks after we built the file
          streamRef.current?.getTracks().forEach(t => t.stop());
          streamRef.current = null;

          resolve(file);
        } catch (err) {
          setStatus("error");
          setError("Failed to finalize recording.");
          resolve(null);
        } finally {
          stopPromiseRef.current = null;
        }
      };

      mr.addEventListener("dataavailable", onData);
      mr.addEventListener("stop", onStop, { once: true });

      try {
        // Flush last chunk first
        if ((mr as any).requestData) { try { (mr as any).requestData(); } catch {} }
        // Safari: give 'dataavailable' a tick before stopping
        setTimeout(() => {
          try { mr.stop(); } catch (err) {
            mr.removeEventListener("dataavailable", onData);
            mr.removeEventListener("stop", onStop);
            stopPromiseRef.current = null;
            reject(err);
          }
        }, 0);
      } catch (err) {
        mr.removeEventListener("dataavailable", onData);
        mr.removeEventListener("stop", onStop);
        stopPromiseRef.current = null;
        reject(err);
      }
    });

    return stopPromiseRef.current;
  }, [mimeType]);

  // ------------------------------------------------------------------------------------------
  const reset = useCallback(() => {
    chunksRef.current = [];
    baseElapsedMsRef.current = 0;
    startedAtRef.current = null;
    setDurationMs(0);
    setStatus("idle");
    setError(null);
    clearTick();
    try { mrRef.current?.stop(); } catch {}
    mrRef.current = null;
    stopStream();
    stopPromiseRef.current = null;
  }, []);

  return {
    status,
    error,
    mimeType,
    durationMs,
    start,
    pause,
    resume,
    stop,   // async
    reset,
  };
}
